"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * A SHELF OF SLIDES ON A NATIVE SCROLL-SNAP TRACK — the drops' launch pad and
 * the journal's «در قاب امروز». The swipe itself is the platform's (touch,
 * trackpad, keys); this only reads where the track is and moves it for the
 * arrows and dots.
 *
 * How many slides show at once is the CSS's business (two side by side on a
 * wide screen, one with the next peeking on a phone); it is read back from the
 * slides' width, so the dots are the positions the track can actually rest at
 * — five drops two at a time is four stops, not five.
 */
export function useSnapRail(count) {
  const $ = _c(11);
  const track = useRef(null);
  const [index, setIndex] = useState(0);
  const [perView, setPerView] = useState(1);
  let t0;
  if ($[0] !== count) {
    t0 = () => {
      const el = track.current;
      const first = el?.children[0] as HTMLElement | undefined;
      if (!el || !first) {
        return;
      }
      const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
      const step = first.offsetWidth + gap;
      setPerView(Math.max(1, Math.round((el.clientWidth + gap) / step)));
      setIndex(Math.min(count - 1, Math.max(0, Math.round(Math.abs(el.scrollLeft) / step))));
    };
    $[0] = count;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const measure = t0;
  let t1;
  let t2;
  if ($[2] !== measure) {
    t1 = () => {
      const el_0 = track.current;
      if (!el_0) {
        return;
      }
      let frame = 0;
      const onScroll = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(measure);
      };
      const ro = new ResizeObserver(onScroll);
      ro.observe(el_0);
      el_0.addEventListener("scroll", onScroll, {
        passive: true
      });
      measure();
      return () => {
        cancelAnimationFrame(frame);
        ro.disconnect();
        el_0.removeEventListener("scroll", onScroll);
      };
    };
    t2 = [measure];
    $[2] = measure;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  const stops = Math.max(1, count - perView + 1);
  const at = Math.min(index, stops - 1);
  let t3;
  if ($[5] !== stops) {
    t3 = i => {
      const el_1 = track.current;
      if (!el_1) {
        return;
      }
      const target = (i % stops + stops) % stops;
      const slide = el_1.children[target] as HTMLElement | undefined;
      if (!slide) {
        return;
      }
      const rtl = getComputedStyle(el_1).direction === "rtl";
      const s = slide.getBoundingClientRect();
      const t = el_1.getBoundingClientRect();
      el_1.scrollBy({
        left: rtl ? s.right - t.right : s.left - t.left,
        behavior: "smooth"
      });
    };
    $[5] = stops;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const go = t3;
  let t4;
  if ($[7] !== at || $[8] !== go || $[9] !== stops) {
    t4 = {
      track,
      index: at,
      stops,
      go
    };
    $[7] = at;
    $[8] = go;
    $[9] = stops;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}